import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useTranslation } from 'react-i18next';
import MyGroupsContextProvider from './context/MyGroupsContext';
import useGroups, { SchoolYear } from './hooks/useGroups';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import PageLayout from '../../layout/Page.ianua';
import OverviewMyGroups from './components/OverviewMyGroups';
import ContentHeader from '../../layout/content/ContentHeader';
import ContentBody from '../../layout/content/ContentBody';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';

const getSchoolYearDisplayName = (schoolYear: SchoolYear) => {
  if (schoolYear.hasPassed) return 'PAGES.MY_GROUPS.TAB_LIST.TABS.PREVIOUS';
  if (schoolYear.isCurrent) return 'PAGES.MY_GROUPS.TAB_LIST.TABS.CURRENT';
  return 'PAGES.MY_GROUPS.TAB_LIST.TABS.NEXT';
};

interface MyGroupsProps extends WithScrollThresholdProps {}

export function MyGroups({ pastThreshold }: MyGroupsProps) {
  const { t } = useTranslation();
  const { schoolYears, selectedSchoolYear, onSchoolYearChange } = useGroups();

  useDocumentTitle(t('PAGES.MY_GROUPS.PAGE_TITLE'));

  const updateSelectedSchoolYear = (schoolYearId: string) => {
    const newSchoolYear = schoolYears.find(schoolYear => schoolYear.id === schoolYearId);

    if (newSchoolYear) {
      onSchoolYearChange(newSchoolYear);
    }
  };

  return (
    <PageLayout>
      <Page>
        <Tabs selectedTab={selectedSchoolYear?.id} onTabChange={updateSelectedSchoolYear}>
          <ContentHeader
            cover={
              <Page.Header
                density={pastThreshold ? 'compact' : 'normal'}
                headerText={t('PAGES.MY_GROUPS.TITLE')}
                extra={
                  schoolYears.length ? (
                    <Tabs.TabList
                      aria-label={t('PAGES.MY_GROUPS.TAB_LIST.A11Y_LABEL')}
                      size={pastThreshold ? 'sm' : 'md'}
                    >
                      {schoolYears.map(schoolYear => (
                        <Tabs.Tab key={schoolYear.id} id={schoolYear.id}>
                          {t(getSchoolYearDisplayName(schoolYear))}
                        </Tabs.Tab>
                      ))}
                    </Tabs.TabList>
                  ) : undefined
                }
              />
            }
          />
          <ContentBody>
            <Tabs.TabsContent>
              {schoolYears.map(schoolYear => (
                <Tabs.TabPanel key={schoolYear.id} id={schoolYear.id}>
                  <OverviewMyGroups schoolYear={schoolYear} />
                </Tabs.TabPanel>
              ))}
            </Tabs.TabsContent>
          </ContentBody>
        </Tabs>
      </Page>
    </PageLayout>
  );
}

interface MyGroupsWithContextProps extends WithScrollThresholdProps {}

function MyGroupsWithContext({ pastThreshold }: MyGroupsWithContextProps) {
  return (
    <MyGroupsContextProvider>
      <MyGroups pastThreshold={pastThreshold} />
    </MyGroupsContextProvider>
  );
}

export default withScrollThreshold(MyGroupsWithContext);
